import React, { useMemo, useState } from "react";

// @material-ui/core components
import { makeStyles } from "tss-react/mui";
import { FormControl, Select, MenuItem, Box, Chip, TextField, FormHelperText } from "@mui/material";

// core components
import Button from "../CustomButtons/Button.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

// sections for this page
import ForumsSuccessModal from "../ForumsSuccessModal/ForumsSuccessModal.js";
import ForumsLexicalEditor from "../ForumsLexical/ForumsLexicalEditor.js";
import ForumsModal from "../ForumsModal/ForumsModal";
import ForumsBlockerModal from "../ForumsBlockerModal/ForumsBlockerModal";

import styles from "../../styles/jss/nextjs-material-kit/pages/forumsStyle.js";
import { useForumsAddTopicRequest, useForumsTagsRequest } from "../../request/api";
import { useForumContext } from "../../contexts/forum";

const useStyles = makeStyles()(styles);

export function ForumsTopicAddPost(props) {

  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { classes, cx } = useStyles();
  const onClose = props.onClose;
  const setSuccessModalOpen = props.setSuccessModalOpen;

  const [newPost, setNewPost] = useState(`<p></p>`);
  const [newTitle, setNewTitle] = useState(``);

  const [isBlocked, setIsBlocked] = React.useState(false);

  const { data: allTags, isLoading: isLoadingTags } = useForumsTagsRequest();

  const [selectedTags, setSelectedTags] = useState([]);

  const { forums, reloadForums } = useForumContext();

  const {
    isMutating: isAddingPost,
    trigger: addPost,
    error: addPostError,
  } = useForumsAddTopicRequest(
    { subCatId: forums.category.subCatId },
    undefined,
    {
      withCredentials: true,
    }
  );



  const handleTagChange = (event) => {
    if (selectedTags.includes(event.target.value)) {
      setSelectedTags(selectedTags.filter(tag => tag !== event.target.value));
    } else {
      setSelectedTags([...selectedTags, event.target.value]);
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSuccessModalOpen(false);
    setErrors({});

    try {
      const res = await addPost({
        body: {
          title: newTitle,
          content: newPost,
          tags: selectedTags,
        },
      }).then((res) => res.data);

      window.gtag && window.gtag("event", "forum_topic_submit");

      if (res?.success) {
        if (res?.status === "unapproved") {
          setIsBlocked(true);
          setIsSubmitting(false);
        } else {
          reloadForums();
          onClose();
          setSuccessModalOpen(true);
        }
      }
    } catch (e) {
      console.error(e);
      setErrors(e.response?.data?.errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsBlocked(false);
    reloadData();
    onClose();
  };

  return (
    <>
      <form className={classes.addPostModal} onSubmit={handleSubmit}>
        <h3 className={classes.modalTitle}>Ask Question</h3>
        <div className={classes.modalScrollable}>
        <FormControl fullWidth>
          <p className={classes.formLabel}>Question</p>
          <TextField
            id="title"
            name="title"
            value={newTitle}
            placeholder="Type your question here"
            required
            onChange={(e) => setNewTitle(e.target.value)}
            className={classes.textInput}
          />
          <FormHelperText error={!!errors?.title}>
            {errors?.title}
          </FormHelperText>
          <p className={classes.formLabel}>Description</p>
          <ForumsLexicalEditor newPost={newPost} setNewPost={setNewPost} />
          <FormHelperText error={!!errors?.content}>
            {errors?.content}
          </FormHelperText>
          <p className={classes.formLabel}>Add up to 5 tags to help others find your post:</p>
          <div
            className={classes.topicTagsContainer}
          >
            {(allTags ?? []).map(({ name: tag, id: tagId }, index) => {
              const selected = selectedTags.includes(tag)
              return (
                <Button
                  value={tag}
                  key={index}
                  className={selected === true ? classes.addTagSelected : classes.addTag}
                  disabled={selectedTags.length>4 && !selected}
                  onClick={handleTagChange}>
                  {tag}
                  {selected &&
                    <FontAwesomeIcon icon={faTimes} className={classes.embeddedClose} />}
                </Button>
              )
            })}
          </div>
          <div className={classes.editorButtons}>
            <Button type="submit" className={cx(classes.darkButton + " " + classes.mediumButton)}>
              {isSubmitting ? <>
                Submitting
              </> : <>
                Submit
              </>}
            </Button>
            <Button className={classes.cancelButton} onClick={() => onClose()}>Cancel</Button>
          </div>
        </FormControl>
        </div>
      </form>
      <ForumsBlockerModal type="language" open={isBlocked} onClose={handleClose} />

    </>
  )
}

export default function ForumsTopicAddPostWrapper(props) {
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const { onClose, topic, community, open, ...rest } = props;
  const { classes, cx } = useStyles();

  const { forums } = useForumContext();

  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
    onClose();
  }

  return (<>
    <ForumsModal open={open} onClose={onClose} closeButton={true} width={classes.width50}>
      <ForumsTopicAddPost topic={topic} open={true} onClose={onClose} setSuccessModalOpen={setSuccessModalOpen} community={community} {...rest} />
    </ForumsModal>
    <ForumsSuccessModal open={successModalOpen} onClose={handleSuccessClose} type="question" community={forums?.category?.name} />
  </>);
};
